import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import {
  Box,
  Button,
  Card,
  Flex,
  Separator,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import Loading from '@components/Loading';
import ErrorLabel from '@components/ErrorLabel';
import { formatMoney } from '@utils/formats';
import {
  ContractStatus,
  CONTRACT_STATUS_FLOW,
  CONTRACT_STATUS_LABEL,
  type ContractType,
} from '@customTypes/contract';

const ContractStepper = () => {
  const location = useLocation();
  const customerInteractionId = location.state?.customerInteractionId;

  const [contract, setContract] = useState<ContractType | null>(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');

  const getContract = useCallback(async () => {
    if (!customerInteractionId) return;
    setLoading(true);
    try {
      const response = await api.get('/contract', {
        params: { customerInteractionId },
      });
      const list: ContractType[] = response.data?.data ?? response.data ?? [];
      if (list.length > 0) setContract(list[0]);
    } catch (error) {
      datadogRum.addError(new Error('Fail to load contract'), { error, customerInteractionId });
    } finally {
      setLoading(false);
    }
  }, [customerInteractionId]);

  useEffect(() => {
    getContract();
  }, [getContract]);

  if (!customerInteractionId) return null;
  if (loading) return <Loading />;
  if (!contract) return null;

  const currentStatus = contract.status;
  const isCancelled = currentStatus === ContractStatus.Cancelled;
  const isPaid = currentStatus === ContractStatus.Paid;
  const currentIndex = CONTRACT_STATUS_FLOW.indexOf(currentStatus);
  const canAdvance = !isCancelled && !isPaid;
  const isLastStep = currentIndex === CONTRACT_STATUS_FLOW.length - 1;

  const nextStatus = !isLastStep ? CONTRACT_STATUS_FLOW[currentIndex + 1] : null;

  const handleAdvance = async () => {
    if (!nextStatus && nextStatus !== 0) return;

    setCancelReasonError('');
    setAdvancing(true);
    try {
      const response = await api.patch(`/contract/${contract.id}/status`, {
        status: nextStatus,
      });
      if (response.status === 200) {
        toast.success('Status do contrato atualizado!');
        await getContract();
        setCancelReason('');
      }
    } catch (error) {
      toast.error('Erro ao atualizar status do contrato.');
      datadogRum.addError(new Error('Fail to advance contract status'), {
        error,
        contractId: contract.id,
        nextStatus,
      });
    } finally {
      setAdvancing(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      setCancelReasonError('Informe o motivo do cancelamento.');
      return;
    }
    setCancelReasonError('');
    setAdvancing(true);
    try {
      const response = await api.patch(`/contract/${contract.id}/status`, {
        status: ContractStatus.Cancelled,
        cancel_reason: cancelReason.trim(),
      });
      if (response.status === 200) {
        toast.success('Contrato cancelado.');
        await getContract();
        setCancelReason('');
      }
    } catch (error) {
      toast.error('Erro ao cancelar contrato.');
      datadogRum.addError(new Error('Fail to cancel contract'), {
        error,
        contractId: contract.id,
        cancelReason,
      });
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <Card.Root size="sm" variant="subtle" p={4} mt={4}>
      <Text fontSize="md" fontWeight="bold">
        Esteira do contrato
      </Text>
      <Separator mb={6} mt={1} />

      <Flex gap={2} alignItems="center" flexWrap="wrap" mb={6}>
        {CONTRACT_STATUS_FLOW.map((step, index) => {
          const isCompleted = currentIndex > index;
          const isCurrent = currentIndex === index;
          const isReached = isCompleted || isCurrent;

          return (
            <Flex key={step} alignItems="center" gap={2}>
              <Flex direction="column" alignItems="center" gap={1}>
                <Flex
                  w={8}
                  h={8}
                  borderRadius="full"
                  border="2px solid"
                  borderColor={isCancelled ? 'red.500' : isReached ? 'green.600' : 'border.muted'}
                  bg={isCancelled && isCurrent ? 'red.500' : isCompleted ? 'green.600' : isCurrent ? 'green.600' : 'transparent'}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color={isReached ? 'white' : 'fg.muted'}
                  >
                    {index + 1}
                  </Text>
                </Flex>
                <Text
                  fontSize="xs"
                  fontWeight={isCurrent ? 700 : 400}
                  color={isCurrent ? 'fg.default' : 'fg.muted'}
                  textAlign="center"
                  maxW={20}
                >
                  {CONTRACT_STATUS_LABEL[step]}
                </Text>
              </Flex>
              {index < CONTRACT_STATUS_FLOW.length - 1 && (
                <Box
                  w={8}
                  h={0.5}
                  bg={isCompleted ? 'green.600' : 'border.muted'}
                  mb={5}
                />
              )}
            </Flex>
          );
        })}

        {isCancelled && (
          <Flex alignItems="center" gap={2}>
            <Box w={8} h={0.5} bg="red.500" mb={5} />
            <Flex direction="column" alignItems="center" gap={1}>
              <Flex
                w={8}
                h={8}
                borderRadius="full"
                border="2px solid"
                borderColor="red.500"
                bg="red.500"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="xs" fontWeight="bold" color="white">✕</Text>
              </Flex>
              <Text fontSize="xs" fontWeight={700} color="red.500" textAlign="center" maxW={20}>
                {CONTRACT_STATUS_LABEL[ContractStatus.Cancelled]}
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>

      <Flex gap={2} mb={4} wrap="wrap">
        <Text fontSize="sm" color="fg.muted">Valor negociado:</Text>
        <Text fontSize="sm" fontWeight={600}>{formatMoney(contract.negotiated_amount)}</Text>
        <Text fontSize="sm" color="fg.muted" ml={4}>Parcelas:</Text>
        <Text fontSize="sm" fontWeight={600}>
          {contract.installment_count}x de {formatMoney(contract.installment_value)}
        </Text>
      </Flex>

      {isCancelled && contract.cancel_reason && (
        <Box mb={4} p={3} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.200">
          <Text fontSize="sm" color="red.700" fontWeight={500}>
            Motivo do cancelamento: {contract.cancel_reason}
          </Text>
        </Box>
      )}

      {canAdvance && (
        <Box>
          {!isLastStep && nextStatus !== null && (
            <Flex justifyContent="flex-end" gap={2} mt={2}>
              {!isCancelled && (
                <Flex direction="column" gap={2} w="full">
                  <Text fontSize="sm" color="fg.muted">Cancelar contrato:</Text>
                  <Textarea
                    placeholder="Motivo do cancelamento..."
                    rows={2}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    maxLength={500}
                  />
                  {cancelReasonError && <ErrorLabel text={cancelReasonError} />}
                  <Flex justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outline"
                      colorPalette="red"
                      size="sm"
                      onClick={handleCancel}
                      loading={advancing}
                    >
                      Cancelar contrato
                    </Button>
                    <Button
                      bg="green.700"
                      color="white"
                      size="sm"
                      onClick={handleAdvance}
                      loading={advancing}
                    >
                      Avançar para: {CONTRACT_STATUS_LABEL[nextStatus]}
                    </Button>
                  </Flex>
                </Flex>
              )}
            </Flex>
          )}
        </Box>
      )}
    </Card.Root>
  );
};

export default ContractStepper;
